import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { httpsCallable } from 'firebase/functions';
import {
  ArrowRight,
  Bot,
  BrainCircuit,
  Check,
  CheckCircle,
  ChevronDown,
  Copy,
  Dices,
  Download,
  FileText,
  Gift,
  GraduationCap,
  Image as ImageIcon,
  ImagePlus,
  Info,
  Loader2,
  PenTool,
  Plus,
  Printer,
  RefreshCw,
  Scissors,
  Settings2,
  Star,
  Users,
  Wand2,
  X,
} from 'lucide-react';
import { functions } from '../config/firebase';
import { useCalculationsSelector } from '../store/selectors/calculations';
import { downloadHtmlAsPdf } from '../utils/pe-papers/htmlDocumentPdfDownload';

const STORAGE_KEY = 'peone_pescout_data_v2';

const PROTOCOL_PRINT_CSS = `
  body {
    font-family: Inter, Arial, Helvetica, sans-serif;
    color: #111827;
    background: #ffffff;
    line-height: 1.5;
    margin: 0;
    padding: 12mm 14mm;
    font-size: 13px;
  }
  h1 { font-size: 22px; font-weight: 900; margin: 0 0 6px; letter-spacing: -0.02em; }
  h2 { font-size: 16px; font-weight: 700; margin: 0 0 6px; color: #111827; }
  p { margin: 0 0 8px; }
  ul { margin: 0 0 10px; padding-left: 0; list-style: none; }
  li { margin: 0 0 10px; }
  label { display: block; font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em; color: #64748b; margin-bottom: 4px; }
  header { border-bottom: 1px solid #e2e8f0; padding-bottom: 16px; margin-bottom: 8px; }
  section { margin-top: 22px; page-break-inside: avoid; }
  .pescout-mono { font-family: 'JetBrains Mono', ui-monospace, monospace; }
`;
const MASTERCLASS_VIDEO = '/assets/PEScout/recruiting-masterclass-intro.mp4';
const REVERSE_VIDEO = '/assets/PEScout/reverse-recruiting.mp4';

const GOLD = '#E6C299';

type AppMode = 'autopilot' | 'interview';
type FlowStep = 'input' | 'loading' | 'result';
type RoleType = 'fachkraft' | 'meister' | 'azubi';
type Experience = 'rohdiamant' | 'rockstar';
type ToneId = 'empathisch' | 'provokant' | 'edel' | 'humorvoll';
type ScoreKey = 'craft' | 'comms' | 'culture' | 'economics';
type PayMode = 'monthly' | 'hourly';
/** Welche Gesprächsprotokoll-Vorlage */
type ProtocolRole = 'fachkraft' | 'azubi' | 'assistenz' | 'rezeption';

type InterviewPhase = {
  id: string;
  title: string;
  tip: string;
  questions: string[];
};

type ProtocolProfile = {
  id: ProtocolRole;
  label: string;
  short: string;
  showEconomics: boolean;
  payLabel: string;
  intro: string;
  phases: InterviewPhase[];
  scores: { key: ScoreKey; label: string }[];
};

const PROTOCOL_PROFILES: Record<ProtocolRole, ProtocolProfile> = {
  fachkraft: {
    id: 'fachkraft',
    label: 'Fachkraft / Stylist',
    short: 'Mit Totpunkt & Mindest-Soll',
    showEconomics: true,
    payLabel: 'Gehaltsvorstellung',
    intro:
      'Du bewirbst dich als Arbeitgeber. Zeige deine Vision, deine Werte – und rechne Gehalt transparent mit Totpunkt und Mindest-Soll.',
    phases: [
      {
        id: 'phase1',
        title: 'Phase 1 · Wir stellen uns vor (Reverse Recruiting)',
        tip: 'Kein Verhör. Safe Space. Du erzählst zuerst, wer ihr seid und warum jemand gerne bei euch arbeitet.',
        questions: [
          'Kurze Vorstellung von uns: Was macht unseren Salon besonders – und warum suchen wir genau dich?',
          'Was muss dein nächster Arbeitsplatz bieten, damit du die nächsten 5 Jahre glücklich bist?',
          'Was hast du über uns gesehen (Instagram, Google, Freunde) – was hat dich neugierig gemacht?',
        ],
      },
      {
        id: 'phase2',
        title: 'Phase 2 · Handwerk & Alltag',
        tip: 'Konkrete Situationen statt Lebenslauf-Floskeln.',
        questions: [
          'Wie gehst du mit einer Reklamation um, wenn du fachlich hinter dem Ergebnis stehst?',
          'Beschreibe einen typischen vollen Tag – wo brauchst du Unterstützung vom Team?',
          'Welche Leistung machst du besonders gerne – und warum ist das wirtschaftlich sinnvoll?',
        ],
      },
      {
        id: 'phase3',
        title: 'Phase 3 · Teamfit, Belastung & Zahlen',
        tip: 'Totpunkt und Mindest-Soll offen zeigen – als gemeinsame Rechnung, nicht als Druck.',
        questions: [
          'Wie gehst du mit Stressspitzen und personellen Ausfällen um?',
          'Lass uns deine Gehaltsvorstellung transparent rechnen: Totpunkt vs. gesunder Sollumsatz – wie fühlst du dich damit?',
          'Wo möchtest du in drei Jahren stehen – und wie können wir dich dorthin begleiten?',
        ],
      },
    ],
    scores: [
      { key: 'craft', label: 'Fachlicher Eindruck & Handwerk' },
      { key: 'comms', label: 'Kommunikation & Auftreten' },
      { key: 'culture', label: 'Team- & Kulturfit' },
      { key: 'economics', label: 'Wirtschaftliche Passung (Sollumsatz)' },
    ],
  },
  azubi: {
    id: 'azubi',
    label: 'Auszubildende/r',
    short: 'Ohne Umsatzzahlen – Fokus Lernen & Haltung',
    showEconomics: false,
    payLabel: 'Ausbildungsvergütung (optional)',
    intro:
      'Azubis tragen keinen Bedienumsatz. Hier geht es um Motivation, Respekt, Lernwillen und ob wir als Ausbildungsbetrieb überzeugen.',
    phases: [
      {
        id: 'phase1',
        title: 'Phase 1 · Wir stellen uns vor (Reverse Recruiting)',
        tip: 'Du bewirbst dich als Ausbildungsbetrieb. Zeige, dass Azubis Mitgestalter sind – keine Besenschwinger.',
        questions: [
          'Unsere Vorstellung: Wie läuft Ausbildung bei uns ab Tag 1 – Lernen, Kunden, Mentoring?',
          'Warum Friseurhandwerk – und warum genau bei uns?',
          'Was muss ein Ausbildungsplatz bieten, damit du dich sicher und gesehen fühlst?',
        ],
      },
      {
        id: 'phase2',
        title: 'Phase 2 · Lernen, Haltung & Alltag',
        tip: 'Schule, Üben, Feedback – ohne Druck-Pose.',
        questions: [
          'Wie gehst du mit Kritik um, wenn etwas noch nicht sitzt?',
          'Was motiviert dich an Tagen, an denen viel geübt und wenig „geglänzt“ wird?',
          'Wie organisierst du Berufsschule, Praxis und Freizeit – was brauchst du von uns?',
        ],
      },
      {
        id: 'phase3',
        title: 'Phase 3 · Teamfit, Verantwortung & Zukunft',
        tip: 'Loyalität entsteht durch echte Verantwortung – nicht durch leere Versprechen.',
        questions: [
          'Wie gehst du mit Stress oder Fehlern um, wenn der Salon voll ist?',
          'Welche Verantwortung würdest du gerne früher übernehmen (Empfang, Farbmischen, Social Media…)?',
          'Wo siehst du dich nach der Ausbildung – und wie können wir dich dorthin begleiten?',
        ],
      },
    ],
    scores: [
      { key: 'craft', label: 'Lernmotivation & handwerkliches Interesse' },
      { key: 'comms', label: 'Kommunikation & Auftreten' },
      { key: 'culture', label: 'Teamfit & Zuverlässigkeit' },
      { key: 'economics', label: 'Entwicklungsperspektive & Passung zur Ausbildung' },
    ],
  },
  assistenz: {
    id: 'assistenz',
    label: 'Assistenz / Salonhilfe',
    short: 'Ohne Bedienumsatz – Fokus Support & Flow',
    showEconomics: false,
    payLabel: 'Gehaltsvorstellung',
    intro:
      'Assistenz trägt den Salon taktisch – nicht über Bedienumsatz. Fragen zu Zuverlässigkeit, Tempo, Team und Gästefluss.',
    phases: [
      {
        id: 'phase1',
        title: 'Phase 1 · Wir stellen uns vor (Reverse Recruiting)',
        tip: 'Zeige, wie wertvoll Assistenz bei euch ist – nicht „nur Helfen“.',
        questions: [
          'Unsere Vorstellung: Welche Rolle spielt Assistenz bei uns im Tagesablauf?',
          'Was muss dein Arbeitsplatz bieten, damit du dich langfristig wohlfühlst?',
          'Was hast du über uns mitbekommen – und was spricht dich an?',
        ],
      },
      {
        id: 'phase2',
        title: 'Phase 2 · Alltag, Tempo & Qualität',
        tip: 'Waschtische, Vorbereitung, Sauberkeit, Timing.',
        questions: [
          'Wie priorisierst du, wenn drei Dinge gleichzeitig passieren?',
          'Wie gehst du mit einem gestressten Team oder ungeduldigen Gästen um?',
          'Welche Aufgaben machst du besonders gerne – und welche eher nicht?',
        ],
      },
      {
        id: 'phase3',
        title: 'Phase 3 · Teamfit, Zuverlässigkeit & Rahmen',
        tip: 'Stunden, Samstage, Vertretung – klar und ehrlich.',
        questions: [
          'Wie gehst du mit Ausfällen und Spitzentagen um?',
          'Welche Arbeitszeiten / Rahmenbedingungen sind für dich gesetzt?',
          'Was brauchst du vom Team, damit du gut performen kannst?',
        ],
      },
    ],
    scores: [
      { key: 'craft', label: 'Sorgfalt & handwerkliche Unterstützung' },
      { key: 'comms', label: 'Kommunikation & Gästehaltung' },
      { key: 'culture', label: 'Teamfit & Zuverlässigkeit' },
      { key: 'economics', label: 'Rahmenpassung (Zeiten, Belastung, Einsatz)' },
    ],
  },
  rezeption: {
    id: 'rezeption',
    label: 'Rezeption & Salon-Management',
    short: 'Ohne Bedienumsatz – Fokus Organisation & Gäste',
    showEconomics: false,
    payLabel: 'Gehaltsvorstellung',
    intro:
      'Rezeption ist der digitale und echte Empfang. Fokus: Organisation, Telefon, Konflikte, Teamfluss – nicht Bedienumsatz.',
    phases: [
      {
        id: 'phase1',
        title: 'Phase 1 · Wir stellen uns vor (Reverse Recruiting)',
        tip: 'Zeige, dass Rezeption bei euch Führungs- und Taktgeberrolle hat.',
        questions: [
          'Unsere Vorstellung: Wie steuert die Rezeption bei uns den Tag?',
          'Was muss dein Arbeitsplatz bieten, damit du langfristig bleibst?',
          'Was hast du über uns gesehen – was hat dich angesprochen?',
        ],
      },
      {
        id: 'phase2',
        title: 'Phase 2 · Gäste, Telefon & Systeme',
        tip: 'Wartezeiten, Beschwerden, Terminchaos – konkrete Situationen.',
        questions: [
          'Wie managst du Wartezeiten und Überziehung, ohne Gast oder Team zu brüskieren?',
          'Wie gehst du mit telefonischen Beschwerden um?',
          'Welche Routine hältst du für unverzichtbar, damit der Salon taktisch ruhig läuft?',
        ],
      },
      {
        id: 'phase3',
        title: 'Phase 3 · Teamfit, Belastung & Rahmen',
        tip: 'Druckspitzen, Samstage, Schnittstelle Team/Inhaber.',
        questions: [
          'Wie gehst du mit Stressspitzen und personellen Ausfällen um?',
          'Wie kommunizierst du unangenehme Themen an das Team oder die Leitung?',
          'Welche Gehalts- und Zeitvorstellung hast du – und was ist dir wichtiger als Geld?',
        ],
      },
    ],
    scores: [
      { key: 'craft', label: 'Organisation & Prozesssicherheit' },
      { key: 'comms', label: 'Kommunikation & Konfliktfähigkeit' },
      { key: 'culture', label: 'Teamfit & Servicehaltung' },
      { key: 'economics', label: 'Rahmenpassung (Zeiten, Verantwortung, Belastung)' },
    ],
  },
};

const PROTOCOL_ROLE_ORDER: ProtocolRole[] = [
  'fachkraft',
  'azubi',
  'assistenz',
  'rezeption',
];

interface InterviewState {
  candidateName: string;
  talkDate: string;
  weeklyHours: string;
  protocolRole: ProtocolRole;
  payMode: PayMode;
  salaryGross: string;
  hourlyRate: string;
  /** Lohnfaktor / Plan-Multiplikator (Mindest-Sollumsatz) */
  multiplier: number;
  multiplierManual: boolean;
  /** Kostendeckungsfaktor (Totpunkt-Umsatz) */
  costFactor: number;
  costFactorManual: boolean;
  notes: string;
  /** Kurznotizen je Leitfragen-Phase */
  phaseNotes: Record<string, string>;
  scores: Record<ScoreKey, number>;
}

interface Persisted {
  mode: AppMode;
  openModule: string | null;
  roleTitle: string;
  roleType: RoleType;
  experience: Experience;
  tone: ToneId;
  selectedBenefits: string[];
  customBenefits: string[];
  campaignText: string;
  /** Explizite Bild-Headline (separates API-Feld, nicht nur im Prompt-Text) */
  headline: string;
  /** Reine Szenenbeschreibung ohne Text-Overlay (optional, aus Campaign) */
  scenePrompt: string;
  imagePrompt: string;
  imageUrl: string | null;
  interview: InterviewState;
}

const clampMultiplier = (n: number) =>
  Math.min(5.5, Math.max(3.5, Math.round(n * 10) / 10));

/** Kostendeckung typ. ~2.5–3.5; sinnvolle Justier-Range */
const clampCostFactor = (n: number) =>
  Math.min(4.5, Math.max(2.0, Math.round(n * 10) / 10));

const defaultInterview = (): InterviewState => ({
  candidateName: '',
  talkDate: '',
  weeklyHours: '35',
  protocolRole: 'fachkraft',
  payMode: 'monthly',
  salaryGross: '',
  hourlyRate: '',
  multiplier: 3.5,
  multiplierManual: false,
  costFactor: 2.8,
  costFactorManual: false,
  notes: '',
  phaseNotes: {},
  scores: { craft: 0, comms: 0, culture: 0, economics: 0 },
});

const defaultState = (): Persisted => ({
  mode: 'autopilot',
  openModule: null,
  roleTitle: '',
  roleType: 'fachkraft',
  experience: 'rohdiamant',
  tone: 'empathisch',
  selectedBenefits: [],
  customBenefits: [],
  campaignText: '',
  headline: '',
  scenePrompt: '',
  imagePrompt: '',
  imageUrl: null,
  interview: defaultInterview(),
});

const loadState = (): Persisted => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaultState();
    const parsed = JSON.parse(raw);
    const merged: Persisted = {
      ...defaultState(),
      ...parsed,
      interview: { ...defaultInterview(), ...(parsed.interview || {}) },
    };
    if (!merged.headline?.trim() && merged.campaignText) {
      const firstLine = merged.campaignText
        .split(/\r?\n/)
        .map((l: string) => l.trim())
        .find(Boolean);
      if (firstLine) merged.headline = firstLine;
    }
    return merged;
  } catch {
    return defaultState();
  }
};

const formatEuro = (n: number) =>
  n.toLocaleString('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });

const WEEKS_PER_MONTH = 52 / 12;

const TONE_LABELS: Record<ToneId, string> = {
  empathisch: 'Empathisch & Motivierend (Wärme & Verständnis)',
  provokant: 'Provokant & Herausfordernd (Klartext & Ecken)',
  edel: 'Clean & Hochwertig (Fokus auf Premium)',
  humorvoll: 'Humorvoll & Locker (Bricht das Eis)',
};

const BENEFITS_FACHKRAFT = [
  '4-Tage-Woche',
  'Überdurchschnittlicher Lohn',
  'Leistungslohnsystem',
  'Flexible Arbeitszeiten',
  'Weiterbildungsbudget',
  'Regelmäßige Team-Events',
];

const BENEFITS_AZUBI = [
  'Werkzeug wird gestellt',
  'Arbeiten am Kunden ab Tag 1',
  'Fahrtkostenzuschuss',
  'Übernahmegarantie',
  'Mentoring-Programm',
];

const generateRecruitingCampaign = httpsCallable(functions, 'generateRecruitingCampaign');
const generateRecruitingImage = httpsCallable(functions, 'generateRecruitingImage');

type AccordionModule = {
  id: string;
  num: string;
  title: string;
  gold?: boolean;
  body: React.ReactNode;
};

const PeScoutPage: React.FC = () => {
  const calc = useCalculationsSelector();
  const datasetMultiplier = clampMultiplier(Number(calc?.lohnfaktorAktiv) || 3.5);
  const datasetCostFactor = clampCostFactor(
    Number(calc?.clientData?.lohnfaktorData?.kostendeckungsfaktor1) || 2.8
  );

  const [state, setState] = useState<Persisted>(() => loadState());
  const [flowStep, setFlowStep] = useState<FlowStep>(() =>
    loadState().campaignText ? 'result' : 'input'
  );
  const [customBenefitDraft, setCustomBenefitDraft] = useState('');
  const [copyFlash, setCopyFlash] = useState(false);
  const [refineText, setRefineText] = useState('');
  const [refineBusy, setRefineBusy] = useState(false);
  const [imageBusy, setImageBusy] = useState(false);
  const [imageError, setImageError] = useState<string | null>(null);
  const [scalpelFeedback, setScalpelFeedback] = useState('');
  const [showImageResult, setShowImageResult] = useState(() => !!loadState().imageUrl);
  const [protocolOpen, setProtocolOpen] = useState(false);
  const [isProtocolPdfDownloading, setIsProtocolPdfDownloading] = useState(false);
  const protocolPrintRef = useRef<HTMLDivElement>(null);
  const persistTimer = useRef<number | null>(null);

  // Multiplikator + Kostendeckung aus Datensatz, solange nicht manuell überschrieben
  useEffect(() => {
    setState((prev) => {
      let next = prev.interview;
      let changed = false;
      if (!prev.interview.multiplierManual && Math.abs(prev.interview.multiplier - datasetMultiplier) >= 0.05) {
        next = { ...next, multiplier: datasetMultiplier };
        changed = true;
      }
      if (!prev.interview.costFactorManual && Math.abs(prev.interview.costFactor - datasetCostFactor) >= 0.05) {
        next = { ...next, costFactor: datasetCostFactor };
        changed = true;
      }
      return changed ? { ...prev, interview: next } : prev;
    });
  }, [datasetMultiplier, datasetCostFactor]);

  useEffect(() => {
    if (persistTimer.current) window.clearTimeout(persistTimer.current);
    persistTimer.current = window.setTimeout(() => {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
      } catch {
        /* ignore */
      }
    }, 250);
    return () => {
      if (persistTimer.current) window.clearTimeout(persistTimer.current);
    };
  }, [state]);

  const patch = useCallback((partial: Partial<Persisted>) => {
    setState((prev) => ({ ...prev, ...partial }));
  }, []);

  const patchInterview = useCallback((partial: Partial<InterviewState>) => {
    setState((prev) => ({
      ...prev,
      interview: { ...prev.interview, ...partial },
    }));
  }, []);

  const getProtocolPrintHtml = useCallback(() => {
    const root =
      protocolPrintRef.current ||
      document.getElementById('pescout-protocol-print');
    if (!(root instanceof HTMLElement)) return '';

    const clone = root.cloneNode(true) as HTMLElement;
    const srcAreas = root.querySelectorAll('textarea');
    const dstAreas = clone.querySelectorAll('textarea');
    srcAreas.forEach((src, i) => {
      const dst = dstAreas[i];
      if (!dst) return;
      const box = document.createElement('div');
      const cls = dst.getAttribute('class');
      if (cls) box.setAttribute('class', cls);
      box.style.whiteSpace = 'pre-wrap';
      box.style.minHeight = '3.5em';
      box.textContent = (src as HTMLTextAreaElement).value || '—';
      dst.replaceWith(box);
    });
    clone.querySelectorAll('button').forEach((btn) => {
      const span = document.createElement('span');
      const cls = btn.getAttribute('class');
      if (cls) span.setAttribute('class', cls);
      span.innerHTML = btn.innerHTML;
      btn.replaceWith(span);
    });
    return clone.innerHTML;
  }, []);

  const handleProtocolDownloadPdf = useCallback(async () => {
    if (isProtocolPdfDownloading) return;
    const html = getProtocolPrintHtml();
    if (!html.trim()) {
      window.alert('Protokollinhalt fehlt — bitte Vorschau prüfen.');
      return;
    }
    setIsProtocolPdfDownloading(true);
    try {
      const dateIso = new Date().toISOString().slice(0, 10);
      const namePart = (state.interview.candidateName || 'Protokoll')
        .trim()
        .replace(/[^\w\-äöüÄÖÜß]+/g, '_')
        .slice(0, 40);
      await downloadHtmlAsPdf({
        html,
        filename: `PeScout_Gespraechsprotokoll_${namePart}_${dateIso}.pdf`,
        scrubMode: 'minimal',
        splitPrintBreaks: true,
        extraCss: PROTOCOL_PRINT_CSS,
      });
    } catch (err: unknown) {
      console.error('PeScout PDF-Download fehlgeschlagen:', err);
      const message = err instanceof Error ? err.message : 'Unbekannter Fehler';
      window.alert('PDF konnte nicht heruntergeladen werden: ' + message);
    } finally {
      setIsProtocolPdfDownloading(false);
    }
  }, [getProtocolPrintHtml, isProtocolPdfDownloading, state.interview.candidateName]);

  const handleProtocolPrint = useCallback(() => {
    if (isProtocolPdfDownloading) return;
    const printContent = getProtocolPrintHtml();
    if (!printContent.trim()) {
      window.alert('Protokollinhalt fehlt — bitte Vorschau prüfen.');
      return;
    }

    const iframe = document.createElement('iframe');
    iframe.style.position = 'absolute';
    iframe.style.width = '0px';
    iframe.style.height = '0px';
    iframe.style.border = 'none';
    document.body.appendChild(iframe);

    const printWindow = iframe.contentWindow;
    if (!printWindow) {
      document.body.removeChild(iframe);
      return;
    }

    const doc = printWindow.document;
    doc.open();
    doc.write(`<!DOCTYPE html><html lang="de"><head><meta charset="utf-8"/><title>PeScout Gesprächsprotokoll</title>
<style>${PROTOCOL_PRINT_CSS}</style></head><body>${printContent}</body></html>`);
    doc.close();

    window.setTimeout(() => {
      printWindow.focus();
      printWindow.print();
      window.setTimeout(() => {
        if (iframe.parentNode) document.body.removeChild(iframe);
      }, 1000);
    }, 400);
  }, [getProtocolPrintHtml, isProtocolPdfDownloading]);

  const toggleModule = (id: string) => {
    patch({ openModule: state.openModule === id ? null : id });
  };

  const benefitPool =
    state.roleType === 'azubi' ? BENEFITS_AZUBI : BENEFITS_FACHKRAFT;

  const toggleBenefit = (b: string) => {
    setState((prev) => ({
      ...prev,
      selectedBenefits: prev.selectedBenefits.includes(b)
        ? prev.selectedBenefits.filter((x) => x !== b)
        : [...prev.selectedBenefits, b],
    }));
  };

  const addCustomBenefit = () => {
    const v = customBenefitDraft.trim();
    if (!v) return;
    setState((prev) => ({
      ...prev,
      customBenefits: prev.customBenefits.includes(v)
        ? prev.customBenefits
        : [...prev.customBenefits, v],
    }));
    setCustomBenefitDraft('');
  };

  const buildTonePayload = (extraRefine?: string) => {
    let tone = TONE_LABELS[state.tone];
    const level =
      state.roleType === 'azubi'
        ? 'Suche Azubi.'
        : state.experience === 'rohdiamant'
          ? 'Suche Rohdiamant zum Aufbauen, nimm aktiv die Angst vor Überforderung!'
          : 'Suche absoluten Vollprofi, Fokus auf Premium-Niveau und Autonomie.';
    tone += ` | LEVEL-PSYCHOLOGIE: ${level}`;
    if (extraRefine) {
      tone += ` | WICHTIGE REGIE-ANWEISUNG DES KUNDEN ZUR ÜBERARBEITUNG: ${extraRefine}`;
    }
    return tone;
  };

  const allBenefits = () => [...state.selectedBenefits, ...state.customBenefits];

  const runGenerateText = async (refine?: string) => {
    if (!state.roleTitle.trim()) {
      window.alert('Bitte gib die Berufsbezeichnung ein.');
      return;
    }
    if (!refine) {
      setFlowStep('loading');
    } else {
      setRefineBusy(true);
    }
    try {
      const result = await generateRecruitingCampaign({
        roleTitle: state.roleTitle.trim(),
        roleType: state.roleType,
        tone: buildTonePayload(refine),
        benefits: allBenefits(),
      });
      const data = (result.data || {}) as {
        text?: string;
        headline?: string;
        scenePrompt?: string;
        imagePrompt?: string;
      };
      const text = data.text || '';
      const headlineFromText = text
        .split(/\r?\n/)
        .map((l) => l.trim())
        .find(Boolean);
      patch({
        campaignText: text,
        headline: (data.headline || headlineFromText || '').trim(),
        scenePrompt: data.scenePrompt || '',
        imagePrompt: data.imagePrompt || state.imagePrompt,
      });
      setFlowStep('result');
      setRefineText('');
    } catch (err: unknown) {
      console.error(err);
      const msg = err instanceof Error ? err.message : 'Unbekannter Fehler';
      window.alert(`Fehler bei der KI: ${msg}`);
      if (!refine) setFlowStep('input');
    } finally {
      setRefineBusy(false);
    }
  };

  const runGenerateImage = async (mode: 'initial' | 'skalpell' | 'roulette') => {
    if (!state.imagePrompt) {
      window.alert('Bitte erst einen Text generieren.');
      return;
    }
    const headline = state.headline.trim();
    if (!headline) {
      window.alert(
        'Bitte die Headline für das Bild eintragen (steht üblicherweise in Zeile 1 der Anzeige).'
      );
      return;
    }
    let feedback = '';
    if (mode === 'skalpell') {
      feedback = scalpelFeedback.trim();
      if (!feedback) {
        window.alert('Bitte gib ein Detail ein (z. B. Haarfarbe, Person).');
        return;
      }
    } else if (mode === 'roulette') {
      feedback =
        'ROULETTE-MODUS: Verwirf das aktuelle Bild komplett! Generiere ein VÖLLIG ANDERES, radikal neues visuelles Konzept. Nutze eine komplett andere Kameraperspektive, einen neuen Style und ein überraschendes Setting.';
    }

    setImageBusy(true);
    setImageError(null);
    setShowImageResult(true);
    try {
      const result = await generateRecruitingImage({
        basePrompt: state.imagePrompt,
        feedback,
        headline,
      });
      const data = (result.data || {}) as {
        imageBase64?: string;
        imageUrl?: string;
        newPrompt?: string;
        headline?: string;
      };
      const url =
        data.imageUrl ||
        (data.imageBase64
          ? data.imageBase64.startsWith('data:')
            ? data.imageBase64
            : `data:image/jpeg;base64,${data.imageBase64}`
          : null);
      if (!url) throw new Error('Keine Bilddaten vom Backend');
      patch({
        imageUrl: url,
        // Nächste Regenerierung immer mit newPrompt als basePrompt
        imagePrompt: data.newPrompt || state.imagePrompt,
        headline: (data.headline || headline).trim(),
      });
      setScalpelFeedback('');
    } catch (err: unknown) {
      console.error(err);
      const msg = err instanceof Error ? err.message : 'Bildgenerierung fehlgeschlagen';
      setImageError(msg);
    } finally {
      setImageBusy(false);
    }
  };

  const resetFlow = () => {
    patch({
      campaignText: '',
      headline: '',
      scenePrompt: '',
      imagePrompt: '',
      imageUrl: null,
    });
    setShowImageResult(false);
    setRefineText('');
    setScalpelFeedback('');
    setImageError(null);
    setFlowStep('input');
  };

  const copyCampaign = async () => {
    try {
      await navigator.clipboard.writeText(state.campaignText);
      setCopyFlash(true);
      window.setTimeout(() => setCopyFlash(false), 1800);
    } catch {
      /* ignore */
    }
  };

  const weeklyHours = Number(String(state.interview.weeklyHours).replace(',', '.')) || 0;
  const hourlyRate = Number(String(state.interview.hourlyRate).replace(',', '.')) || 0;
  const salaryMonthlyDirect =
    Number(String(state.interview.salaryGross).replace(',', '.')) || 0;
  const salaryMonthly =
    state.interview.payMode === 'hourly'
      ? hourlyRate * weeklyHours * WEEKS_PER_MONTH
      : salaryMonthlyDirect;
  const totpunktUmsatz = salaryMonthly * state.interview.costFactor;
  const mindestSollUmsatz = salaryMonthly * state.interview.multiplier;
  const protocol =
    PROTOCOL_PROFILES[state.interview.protocolRole] || PROTOCOL_PROFILES.fachkraft;
  const showEconomics = protocol.showEconomics;

  const modules: AccordionModule[] = [
    {
      id: 'm1',
      num: '01',
      title: 'Die Wahrheit über den Mangel',
      gold: true,
      body: (
        <div className="space-y-4 text-base text-slate-300 leading-relaxed">
          <p>
            Der Fachkräftemangel ist real, ja. Aber er trifft nicht alle gleich hart. Betriebe, die
            aus einem Fundament der echten Wertschätzung agieren – sowohl dem Kunden als auch dem
            Team gegenüber – spüren ihn kaum.
          </p>
          <p>
            Sie bekommen Initiativbewerbungen, weil das bestehende Personal erfüllt ist und diese
            Energie nach außen strahlt.
          </p>
          <p>
            Die gute Nachricht für dich: Diese Anziehungskraft ist kein Zufall, sondern eine klare
            Entscheidung. Lass uns aufhören zu „suchen“ und anfangen, ein Magnet für die Besten zu
            werden.
          </p>
        </div>
      ),
    },
    {
      id: 'm2',
      num: '02',
      title: 'Wertschätzende Preise & Lohnstruktur',
      gold: true,
      body: (
        <div className="space-y-4 text-base text-slate-300 leading-relaxed">
          <p>
            Nur wertschätzende Preise führen zu gesunden Lohnstrukturen. Mit unrealistischen Preisen
            vergraulen wir unbewusst gute Mitarbeiter.
          </p>
          <p>
            Wir zeigen ihnen damit, dass die versprochenen Löhne entweder reine Fantasie sind oder
            nur durch ausufernde Akkordarbeit erbracht werden können. Top-Kräfte spüren das sofort.
          </p>
          <p>
            Dein Lohnfaktor in PeOne ist genau dafür da: faire Gehälter und tragfähige Preise in
            Einklang bringen – bevor du jemanden einstellst, den du dir langfristig nicht leisten
            kannst.
          </p>
        </div>
      ),
    },
    {
      id: 'm3',
      num: '03',
      title: 'Der psychologische Köder',
      body: (
        <div className="space-y-4 text-base text-slate-300 leading-relaxed">
          <p>
            Standard-Phrasen wie „Wir suchen dich!“ locken niemanden mehr hinter dem Ofen hervor.
            Social Media ist ein Ort der Flucht – nicht der Jobanzeigen.
          </p>
          <p>
            Wir brauchen einen Pattern Interrupt. Top-Kräfte reagieren nur, wenn sie sich sofort
            verstanden fühlen oder etwas ihre Neugier weckt.
          </p>
          <p>
            Nutze die KI rechts, um diesen visuellen und textlichen Köder auszuwerfen. Erst wenn das
            Bild den Daumen stoppt, öffnet sich der Kandidat für die Botschaft.
          </p>
        </div>
      ),
    },
    {
      id: 'm4',
      num: '04',
      title: 'Dein digitales Wohnzimmer',
      body: (
        <div className="space-y-4 text-base text-slate-300 leading-relaxed">
          <p>
            Bevor ein Bewerber dir schreibt, macht er einen Background-Check: Instagram,
            Google-Bewertungen, Website. Das ist dein digitaler Empfangsbereich.
          </p>
          <p>
            Gestresste Gesichter? Veraltete Preislisten? Schlechte Ausleuchtung? Dann ist der
            Kandidat in drei Sekunden weg.
          </p>
          <p>
            Zeige glückliche Mitarbeiter, exzellente Arbeiten und eure echte Salon-DNA. Sie müssen
            spüren: „Genau hier gehöre ich hin.“
          </p>
        </div>
      ),
    },
    {
      id: 'm5',
      num: '05',
      title: 'Der Paradigmenwechsel (Mindset)',
      body: (
        <div className="space-y-4 text-base text-slate-300 leading-relaxed">
          <div className="p-4 bg-blue-900/20 border border-blue-500/30 rounded-xl">
            <strong className="text-blue-400 block mb-2">Die goldene Regel:</strong>
            <p className="text-sm">
              Der Mitarbeiter bewirbt sich heutzutage nicht mehr bei uns.{' '}
              <strong>Wir bewerben uns bei ihm!</strong> Unsere Anzeige ist unsere
              Bewerbungsmappe an die besten Talente.
            </p>
          </div>
          <p>
            Wer noch „vollständige Unterlagen per Post“ verlangt, hat das Spiel verloren. Ein
            Top-Stylist auf dem Sofa schreibt keinen formellen Lebenslauf.
          </p>
          <p>
            Call-to-Action warm und einfach:{' '}
            <strong>„Schreib uns einfach ein kurzes Hey per WhatsApp.“</strong>
          </p>
        </div>
      ),
    },
    {
      id: 'm6',
      num: '06',
      title: 'Die Anleitung zum Gespräch',
      body: (
        <div className="space-y-4 text-base text-slate-300 leading-relaxed">
          <div className="relative w-full aspect-video rounded-xl overflow-hidden bg-black/40 border border-slate-700 mb-2 shadow-[0_0_20px_rgba(0,0,0,0.3)]">
            <video
              controls
              playsInline
              preload="metadata"
              className="w-full h-full object-cover"
              src={`${REVERSE_VIDEO}#t=0.001`}
            />
          </div>
          <p>
            <strong>Das ist kein Verhör.</strong> Das ist deine Chance, dich als Arbeitgeber zu
            beweisen. Schreibtisch-Verhör zerstört Vertrauen in Sekunden.
          </p>
          <p>
            Safe Space: Kaffee, Augenhöhe. Frage nicht nur „Was kannst du für meinen Salon tun?“,
            sondern:{' '}
            <strong>
              „Wo möchtest du in drei Jahren stehen und wie kann ich dir helfen, genau dorthin zu
              kommen?“
            </strong>
          </p>
          <p>
            Für Zahlen-Check, Leitfragen und Scorecard im Live-Gespräch: Bereich{' '}
            <strong>Interview-Navigator</strong> oben.
          </p>
        </div>
      ),
    },
    {
      id: 'm7',
      num: '07',
      title: 'Das emotionale Onboarding (Der 1. Tag)',
      gold: true,
      body: (
        <div className="space-y-4 text-base text-slate-300 leading-relaxed">
          <p>
            Am ersten Tag entscheidet sich: „War das ein Fehler?“ oder{' '}
            <strong>„Wow, ich bin zu Hause angekommen!“</strong>
          </p>
          <p>
            Platz vorbereitet, handgeschriebenes Willkommen, kleine liebevolle Details – kein
            Hingelotter.
          </p>
          <p>
            <strong>Der virale Effekt:</strong> Wer so abgeholt wird, erzählt es weiter. Dein
            Onboarding wird zu deinem stärksten Recruiting-Tool.
          </p>
        </div>
      ),
    },
    {
      id: 'm8',
      num: '08',
      title: 'Azubis sind unsere Zukunft',
      body: (
        <div className="space-y-4 text-base text-slate-300 leading-relaxed">
          <p>
            Die neue Generation lässt sich nicht mit der Rolle des „Besen-Schwingers“ abspeisen. Sie
            wollen Sinn, Wertschätzung und Entwicklung ab Tag eins.
          </p>
          <p>
            Mache Azubis zu Mitgestaltern. Wer Vertrauen investiert, erntet Loyalität und
            Leidenschaft, die den Salon jahrelang trägt.
          </p>
        </div>
      ),
    },
    {
      id: 'm9',
      num: '09',
      title: 'Die kluge Sichtbarkeit',
      body: (
        <div className="space-y-4 text-base text-slate-300 leading-relaxed">
          <p>
            Text und Bild stehen – jetzt Sichtbarkeit im Einzugsgebiet. Nicht nur teure Ads, sondern
            smarte lokale Hebel:
          </p>
          <ul className="space-y-3 mt-2 text-sm bg-slate-800/20 p-5 rounded-xl border border-slate-700/50">
            {[
              {
                t: 'Facebook-Gruppen',
                d: 'Lokale Gruppen (z. B. „Jobs in …“) – Community-Trust statt kalter Anzeige.',
              },
              {
                t: 'Kleinanzeigen',
                d: 'Unterschätzt, lokal extrem reichweitenstark.',
              },
              {
                t: 'Arbeitsamt',
                d: 'Seriöser Anlaufpunkt für Quereinsteiger und Azubi-Suchende.',
              },
            ].map((x) => (
              <li key={x.t} className="flex items-start gap-3">
                <ArrowRight className="w-4 h-4 text-blue-400 shrink-0 mt-0.5" />
                <span>
                  <strong>{x.t}:</strong> {x.d}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ),
    },
  ];

  return (
    <div className="pescout-root text-slate-300 -mx-2 md:-mx-4 pb-10 animate-[pescoutFade_0.5s_cubic-bezier(0.16,1,0.3,1)_forwards]">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;900&family=JetBrains+Mono:wght@400;700&display=swap');
        .pescout-root { font-family: 'Inter', sans-serif; }
        .pescout-mono { font-family: 'JetBrains Mono', monospace; }
        @keyframes pescoutFade {
          from { opacity: 0; transform: translateY(15px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .pescout-root textarea:focus,
        .pescout-root input:focus,
        .pescout-root select:focus {
          outline: none;
          border-color: #3b82f6 !important;
          box-shadow: 0 0 0 1px #3b82f6, 0 0 20px rgba(59, 130, 246, 0.15);
          background-color: #1e293b !important;
        }
        @media print {
          .pescout-no-print { display: none !important; }
        }
      `}</style>

      <div className="bg-[#0f172a] rounded-[2rem] border border-slate-700 shadow-2xl overflow-hidden relative max-w-7xl mx-auto flex flex-col">
        {/* HEADER */}
        <div className="pescout-no-print bg-[#0a0f1c] border-b border-slate-800 p-6 md:p-8 relative overflow-hidden shrink-0">
          <div className="absolute top-0 right-0 w-80 h-80 bg-blue-600/10 rounded-full blur-3xl pointer-events-none" />
          <div
            className="absolute bottom-0 left-0 w-64 h-64 rounded-full blur-3xl pointer-events-none"
            style={{ backgroundColor: `${GOLD}0D` }}
          />

          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 relative z-10">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-blue-900/20 rounded-2xl flex items-center justify-center border border-blue-500/30 shadow-[0_0_20px_rgba(59,130,246,0.15)] shrink-0">
                <Users className="w-7 h-7 text-blue-400" />
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <h1 className="text-2xl md:text-3xl font-black text-white tracking-tight">
                    <span className="italic font-semibold" style={{ color: GOLD }}>
                      PE
                    </span>
                    Scout
                  </h1>
                  <span
                    className="px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest border rounded"
                    style={{
                      backgroundColor: `${GOLD}33`,
                      color: GOLD,
                      borderColor: `${GOLD}4D`,
                    }}
                  >
                    Premium
                  </span>
                </div>
                <p className="text-xs md:text-sm text-blue-400 font-mono uppercase tracking-widest">
                  Mitarbeiterakquise · Fachkräfte finden & einstellen
                </p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <div className="bg-slate-900/80 border border-slate-700 px-4 py-2 rounded-xl text-xs text-slate-400 flex items-center gap-3 backdrop-blur-sm max-w-sm shadow-inner">
                <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse shrink-0" />
                <span>
                  Links die Masterclass – rechts Schritt für Schritt deine Kampagne. Kein Stress,
                  ein Modul nach dem anderen.
                </span>
              </div>
            </div>
          </div>

          {/* Mode switch – only two calm entries */}
          <div className="relative z-10 mt-6 flex gap-2 p-1 rounded-xl bg-slate-900/60 border border-slate-800 max-w-xl">
            <button
              type="button"
              onClick={() => patch({ mode: 'autopilot' })}
              className={`flex-1 rounded-lg px-4 py-2.5 text-xs font-bold uppercase tracking-wider transition-colors ${
                state.mode === 'autopilot'
                  ? 'bg-blue-600/30 text-blue-300 border border-blue-500/40'
                  : 'text-slate-500 hover:text-slate-300'
              }`}
            >
              Masterclass & Kampagne
            </button>
            <button
              type="button"
              onClick={() => patch({ mode: 'interview' })}
              className={`flex-1 rounded-lg px-4 py-2.5 text-xs font-bold uppercase tracking-wider transition-colors ${
                state.mode === 'interview'
                  ? 'bg-blue-600/30 text-blue-300 border border-blue-500/40'
                  : 'text-slate-500 hover:text-slate-300'
              }`}
            >
              Interview-Navigator
            </button>
          </div>
        </div>

        {/* AUTOPILOT */}
        {state.mode === 'autopilot' && (
          <div className="pescout-no-print grid grid-cols-1 lg:grid-cols-12 gap-0 flex-1 relative z-10">
            {/* LEFT: Masterclass */}
            <div className="lg:col-span-6 border-b lg:border-b-0 lg:border-r border-slate-800 bg-[#020617] p-6 md:p-8 flex flex-col">
              <div className="relative w-full aspect-video rounded-xl overflow-hidden bg-black/40 border border-slate-700 mb-8 shadow-[0_0_30px_rgba(0,0,0,0.5)]">
                <video
                  controls
                  playsInline
                  preload="metadata"
                  className="w-full h-full object-cover"
                  src={`${MASTERCLASS_VIDEO}#t=0.001`}
                />
              </div>

              <div className="mb-6 border-b border-slate-800 pb-6">
                <h2 className="text-xl md:text-2xl font-black text-white mb-2 flex items-center gap-2">
                  <GraduationCap className="w-6 h-6 text-blue-400" /> Masterclass: Recruiting
                </h2>
                <p className="text-base text-slate-400 leading-relaxed">
                  Unsere Branche sucht händeringend nach Leidenschaft. Doch wer im Hamsterrad der
                  falschen Preiskalkulation feststeckt, verbrennt unfreiwillig sein wichtigstes Gut:
                  sein Team. Vergiss 08/15-Anzeigen. Lass uns eine Umgebung erschaffen, in der
                  Wertschätzung Top-Leute magisch anzieht.
                </p>
              </div>

              <div className="space-y-4">
                {modules.map((m) => {
                  const open = state.openModule === m.id;
                  const accent = m.gold ? GOLD : '#60a5fa';
                  return (
                    <div
                      key={m.id}
                      className={`border border-slate-700 bg-[#0a0f1c] rounded-2xl overflow-hidden transition-all duration-300 shadow-lg ${
                        m.gold ? 'hover:border-[#E6C299]/50' : 'hover:border-blue-500/50'
                      }`}
                    >
                      <button
                        type="button"
                        onClick={() => toggleModule(m.id)}
                        className="w-full px-5 py-4 flex items-center justify-between bg-slate-800/20 hover:bg-slate-800/60 transition-colors text-left"
                      >
                        <div className="flex items-center gap-4">
                          <div
                            className="w-10 h-10 rounded-xl flex items-center justify-center border shrink-0"
                            style={{
                              backgroundColor: `${accent}1A`,
                              color: accent,
                              borderColor: `${accent}4D`,
                            }}
                          >
                            <span className="font-black pescout-mono text-sm">{m.num}</span>
                          </div>
                          <span className="font-bold text-white text-base md:text-lg">{m.title}</span>
                        </div>
                        <ChevronDown
                          className={`w-5 h-5 text-slate-500 transition-transform duration-300 shrink-0 ${
                            open ? 'rotate-180' : ''
                          }`}
                        />
                      </button>
                      <div
                        className={`bg-[#020617] transition-all duration-500 overflow-hidden ${
                          open ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
                        }`}
                      >
                        <div className="p-6 border-t border-slate-800">{m.body}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* RIGHT: Generator flow */}
            <div className="lg:col-span-6 bg-[#0a0f1c] p-6 md:p-8 flex flex-col min-h-[480px]">
              {flowStep === 'input' && (
                <div className="space-y-8 animate-[pescoutFade_0.4s_ease]">
                  <h3 className="text-xl font-bold text-white mb-2 flex items-center gap-2">
                    <Bot className="w-6 h-6 text-blue-400" /> KI-Kampagne texten
                  </h3>
                  <p className="text-sm text-slate-500 -mt-4">
                    Schritt 1: Rolle & Benefits wählen → Text erzeugen. Bild kommt danach optional.
                  </p>

                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-bold text-slate-300 mb-2">
                        Wen suchen wir?
                      </label>
                      <input
                        value={state.roleTitle}
                        onChange={(e) => patch({ roleTitle: e.target.value })}
                        placeholder="z.B. Top-Stylist, Kosmetikerin, Barber…"
                        className="w-full bg-[#020617] border border-slate-700 rounded-xl p-3 text-sm text-white mb-4"
                      />

                      <label className="block text-xs uppercase tracking-widest font-bold text-slate-500 mb-2">
                        Art der Anstellung
                      </label>
                      <select
                        value={state.roleType}
                        onChange={(e) => {
                          const roleType = e.target.value as RoleType;
                          patch({
                            roleType,
                            selectedBenefits: [],
                          });
                        }}
                        className="w-full bg-[#020617] border border-slate-700 rounded-xl p-3 text-sm text-slate-300 mb-4"
                      >
                        <option value="fachkraft">Ausgelernte Fachkraft</option>
                        <option value="meister">Meister/Top-Stylist (Fokus: Entfaltung & Lohn)</option>
                        <option value="azubi">Auszubildende/r (Fokus: Respekt & Ausbildung)</option>
                      </select>

                      {state.roleType !== 'azubi' && (
                        <>
                          <label className="block text-xs uppercase tracking-widest font-bold text-slate-500 mb-2 mt-2">
                            Erfahrungs-Level
                          </label>
                          <select
                            value={state.experience}
                            onChange={(e) =>
                              patch({ experience: e.target.value as Experience })
                            }
                            className="w-full bg-[#020617] border border-slate-700 rounded-xl p-3 text-sm text-slate-300 mb-4"
                          >
                            <option value="rohdiamant">
                              Rohdiamant (Wir bauen dich auf / Nehmen Angst vor Überforderung)
                            </option>
                            <option value="rockstar">
                              Fertiger Vollprofi (Fokus auf Premium & Autonomie)
                            </option>
                          </select>
                        </>
                      )}

                      <label className="block text-xs uppercase tracking-widest font-bold text-blue-400 mb-2 mt-2">
                        Tonalität der Anzeige
                      </label>
                      <select
                        value={state.tone}
                        onChange={(e) => patch({ tone: e.target.value as ToneId })}
                        className="w-full bg-[#020617] border border-blue-500/30 rounded-xl p-3 text-sm text-slate-300"
                      >
                        {(Object.keys(TONE_LABELS) as ToneId[]).map((k) => (
                          <option key={k} value={k}>
                            {TONE_LABELS[k]}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="pt-2 border-t border-slate-800">
                      <label className="block text-sm font-bold text-slate-300 mt-4 mb-3 flex items-center gap-2">
                        <Gift className="w-4 h-4" style={{ color: GOLD }} /> Wähle eure stärksten
                        Benefits
                      </label>
                      <p className="text-[10px] text-slate-500 mb-3">
                        Mehrfachauswahl möglich – nur echte Vorteile, keine Floskeln.
                      </p>
                      <div className="flex flex-wrap gap-2 mb-6">
                        {benefitPool.map((b) => {
                          const on = state.selectedBenefits.includes(b);
                          return (
                            <button
                              key={b}
                              type="button"
                              onClick={() => toggleBenefit(b)}
                              className={`px-3 py-1.5 rounded-lg border text-xs font-medium transition-colors ${
                                on
                                  ? 'border-[#E6C299] bg-[#E6C299]/20 text-[#E6C299]'
                                  : 'border-slate-700 bg-[#0a0f1c] text-slate-400 hover:border-[#E6C299]/50'
                              }`}
                            >
                              {b}
                            </button>
                          );
                        })}
                        {state.customBenefits.map((b) => (
                          <button
                            key={b}
                            type="button"
                            onClick={() =>
                              patch({
                                customBenefits: state.customBenefits.filter((x) => x !== b),
                              })
                            }
                            className="px-3 py-1.5 rounded-lg border border-[#E6C299] bg-[#E6C299]/20 text-xs font-medium text-[#E6C299] flex items-center gap-1"
                          >
                            {b} <X className="w-3 h-3" />
                          </button>
                        ))}
                      </div>

                      <label className="block text-xs uppercase tracking-widest font-bold text-slate-500 mb-2">
                        Weitere, individuelle Benefits?
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          value={customBenefitDraft}
                          onChange={(e) => setCustomBenefitDraft(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              addCustomBenefit();
                            }
                          }}
                          placeholder="Z.B.: Eigenes iPad…"
                          className="w-full bg-[#020617] border border-slate-700 rounded-xl p-3 text-sm text-white"
                        />
                        <button
                          type="button"
                          onClick={addCustomBenefit}
                          className="p-3 bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded-xl text-white"
                        >
                          <Plus className="w-5 h-5" />
                        </button>
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={() => void runGenerateText()}
                      className="w-full py-4 rounded-xl border border-blue-500 bg-blue-600 hover:bg-blue-500 text-white font-black text-xs md:text-sm uppercase tracking-widest transition-all shadow-[0_0_20px_rgba(59,130,246,0.2)] flex items-center justify-center gap-2 mt-4"
                    >
                      <Wand2 className="w-5 h-5" /> 1. KI Text generieren
                    </button>
                  </div>
                </div>
              )}

              {flowStep === 'loading' && (
                <div className="flex flex-col items-center justify-center flex-1 py-20">
                  <div className="relative w-16 h-16 mb-6">
                    <div className="absolute inset-0 border-4 border-slate-800 rounded-full" />
                    <div className="absolute inset-0 border-4 border-blue-500 rounded-full border-t-transparent animate-spin" />
                    <BrainCircuit className="absolute inset-0 m-auto w-6 h-6 text-blue-400 animate-pulse" />
                  </div>
                  <h3 className="text-white font-bold text-lg mb-2">KI schreibt deinen Text…</h3>
                  <p className="text-slate-500 pescout-mono text-xs uppercase tracking-widest text-center">
                    Analysiere Empathie & Salon-DNA
                  </p>
                </div>
              )}

              {flowStep === 'result' && (
                <div className="flex flex-col space-y-8 animate-[pescoutFade_0.4s_ease] pb-6">
                  <div className="flex items-center justify-between border-b border-slate-800 pb-4 gap-3">
                    <h3 className="text-lg font-bold text-white flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-500" /> Dein Kampagnen-Text
                    </h3>
                    <button
                      type="button"
                      onClick={resetFlow}
                      className="text-[10px] text-slate-500 hover:text-white uppercase tracking-widest font-bold flex items-center gap-1 transition-colors bg-slate-800 px-3 py-1.5 rounded-lg border border-slate-700"
                    >
                      <RefreshCw className="w-3 h-3" /> Neuer Text
                    </button>
                  </div>

                  <div className="flex flex-col gap-2">
                    <textarea
                      readOnly
                      rows={12}
                      value={state.campaignText}
                      className="w-full bg-[#020617] border border-slate-700 rounded-xl p-5 text-sm text-slate-200 leading-relaxed"
                    />
                    <div className="flex justify-end">
                      <button
                        type="button"
                        onClick={() => void copyCampaign()}
                        className="bg-slate-800 hover:bg-slate-700 px-4 py-2 rounded-lg text-white text-xs font-bold flex items-center gap-2 border border-slate-600"
                      >
                        {copyFlash ? (
                          <>
                            <Check className="w-4 h-4 text-green-400" /> Kopiert!
                          </>
                        ) : (
                          <>
                            <Copy className="w-4 h-4" /> Text kopieren
                          </>
                        )}
                      </button>
                    </div>
                  </div>

                  <div className="flex flex-col md:flex-row items-center gap-3 bg-slate-800/20 p-3 rounded-xl border border-slate-700/50">
                    <div className="relative w-full flex-1">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <PenTool className="w-4 h-4 text-slate-500" />
                      </div>
                      <input
                        value={refineText}
                        onChange={(e) => setRefineText(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') void runGenerateText(refineText.trim());
                        }}
                        placeholder="Regie-Anweisung (z.B. kürzer, Salonhund erwähnen…)"
                        className="w-full bg-[#020617] border border-slate-700 rounded-xl pl-10 pr-4 py-3 text-sm text-white"
                      />
                    </div>
                    <button
                      type="button"
                      disabled={refineBusy}
                      onClick={() => void runGenerateText(refineText.trim())}
                      className="w-full md:w-auto px-5 py-3 bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded-xl text-white font-bold text-sm flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                      {refineBusy ? (
                        <Loader2 className="w-4 h-4 animate-spin text-blue-400" />
                      ) : (
                        <RefreshCw className="w-4 h-4 text-blue-400" />
                      )}
                      Ändern
                    </button>
                  </div>

                  <div className="bg-[#020617] border border-slate-800 p-6 rounded-2xl shadow-lg">
                    <h4 className="text-slate-300 font-bold text-base mb-2 flex items-center gap-2">
                      <ImagePlus className="w-5 h-5" style={{ color: GOLD }} /> 2. Passendes Bild
                      dazu? (Optional)
                    </h4>
                    <p className="text-xs text-slate-500 mb-4">
                      Format 3:4 (1080×1440). Headline wird bei jeder Bildgenerierung explizit
                      mitgeschickt – so geht der Text nie verloren.
                    </p>

                    <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1.5">
                      Headline auf dem Bild
                    </label>
                    <input
                      type="text"
                      value={state.headline}
                      onChange={(e) => patch({ headline: e.target.value })}
                      placeholder="z. B. Werden deine Talente verschwendet?"
                      className="w-full bg-[#0f172a] border border-slate-700 rounded-xl px-4 py-3 text-sm text-white mb-4 placeholder:text-slate-600"
                    />

                    {!showImageResult && (
                      <button
                        type="button"
                        disabled={imageBusy}
                        onClick={() => void runGenerateImage('initial')}
                        className="w-full py-4 rounded-xl border border-[#E6C299] bg-[#E6C299]/10 hover:bg-[#E6C299]/20 text-[#E6C299] font-black text-xs md:text-sm uppercase tracking-widest flex items-center justify-center gap-2 mb-4 disabled:opacity-50"
                      >
                        {imageBusy ? (
                          <Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                          <ImageIcon className="w-5 h-5" />
                        )}
                        Konzept als Bild generieren
                      </button>
                    )}

                    {showImageResult && (
                      <div className="mt-2 pt-4 border-t border-slate-800">
                        <div className="relative w-full max-w-sm mx-auto aspect-[3/4] bg-black rounded-xl overflow-hidden border border-slate-700 mb-4 flex items-center justify-center">
                          {imageBusy && (
                            <div className="absolute inset-0 flex flex-col items-center justify-center z-10 bg-black/60">
                              <Loader2 className="w-8 h-8 text-[#E6C299] animate-spin mb-2" />
                              <span className="text-xs text-slate-500 pescout-mono">
                                Warte auf Imagen…
                              </span>
                            </div>
                          )}
                          {state.imageUrl ? (
                            <img
                              src={state.imageUrl}
                              alt="Recruiting KI Bild"
                              className="w-full h-full object-contain opacity-90"
                            />
                          ) : (
                            !imageBusy && (
                              <span className="text-xs text-slate-500 px-4 text-center">
                                {imageError || 'Noch kein Bild'}
                              </span>
                            )
                          )}
                        </div>

                        {state.imageUrl && (
                          <a
                            href={state.imageUrl}
                            download="PreisExpert_Recruiting_Bild.jpg"
                            className="w-full py-4 rounded-xl border border-green-500/50 bg-green-600/20 hover:bg-green-600/30 text-green-400 font-black text-xs md:text-sm uppercase tracking-widest flex items-center justify-center gap-2 mb-6 shadow-[0_0_15px_rgba(34,197,94,0.1)]"
                          >
                            <Download className="w-5 h-5" /> Bild in voller Qualität herunterladen
                          </a>
                        )}

                        <div className="space-y-4 bg-slate-800/20 p-4 rounded-xl border border-slate-700/50">
                          <h5 className="text-sm font-bold text-white flex items-center gap-2 mb-1">
                            <Settings2 className="w-4 h-4 text-blue-400" /> Bild anpassen
                          </h5>
                          <div className="flex flex-col md:flex-row gap-2">
                            <div className="relative w-full flex-1">
                              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <PenTool className="w-4 h-4 text-slate-500" />
                              </div>
                              <input
                                value={scalpelFeedback}
                                onChange={(e) => setScalpelFeedback(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') void runGenerateImage('skalpell');
                                }}
                                placeholder="Detail ändern (z.B. rote Haare…)"
                                className="w-full bg-[#020617] border border-slate-700 rounded-xl pl-10 pr-4 py-3 text-sm text-white"
                              />
                            </div>
                            <button
                              type="button"
                              disabled={imageBusy}
                              onClick={() => void runGenerateImage('skalpell')}
                              className="w-full md:w-auto px-5 py-3 bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded-xl text-white font-bold text-sm flex items-center justify-center gap-2 disabled:opacity-50"
                            >
                              <Scissors className="w-4 h-4 text-blue-400" /> Detail anpassen
                            </button>
                          </div>
                          <div className="relative flex py-2 items-center">
                            <div className="flex-grow border-t border-slate-700" />
                            <span className="flex-shrink-0 mx-4 text-slate-500 text-xs font-bold uppercase tracking-widest">
                              oder
                            </span>
                            <div className="flex-grow border-t border-slate-700" />
                          </div>
                          <button
                            type="button"
                            disabled={imageBusy}
                            onClick={() => void runGenerateImage('roulette')}
                            className="w-full py-3 rounded-xl border border-purple-500/30 bg-purple-500/10 hover:bg-purple-500/20 text-purple-400 font-bold text-sm flex items-center justify-center gap-2 disabled:opacity-50"
                          >
                            <Dices className="w-5 h-5" /> Komplett neues Konzept würfeln
                          </button>
                        </div>

                        <div className="p-3 bg-slate-800/50 border border-slate-700 rounded-xl flex items-start gap-3 mt-6">
                          <Info className="w-5 h-5 text-slate-400 shrink-0" />
                          <p className="text-xs text-slate-300 leading-relaxed">
                            Das Bild wird aus Sicherheitsgründen nicht dauerhaft in der Akte
                            gespeichert. Sofort herunterladen und sichern.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}


        {/* INTERVIEW – Setup + Gesprächsprotokoll-Modal */}
        {state.mode === 'interview' && (
          <div className="pescout-no-print p-6 md:p-8 space-y-6 bg-[#020617]">
            <div className="max-w-3xl space-y-3">
              <h2 className="text-xl font-black text-white">Interview-Navigator</h2>
              <p className="text-sm text-slate-400 leading-relaxed">
                Zuerst die Rolle wählen – dann Eckdaten. Das Gespräch führst du im Protokoll mit dem
                passenden Leitfaden (Reverse Recruiting: wir stellen uns vor).
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 max-w-5xl">
              {PROTOCOL_ROLE_ORDER.map((roleId) => {
                const p = PROTOCOL_PROFILES[roleId];
                const on = state.interview.protocolRole === roleId;
                return (
                  <button
                    key={roleId}
                    type="button"
                    onClick={() =>
                      patchInterview({
                        protocolRole: roleId,
                        phaseNotes: {},
                        scores: { craft: 0, comms: 0, culture: 0, economics: 0 },
                      })
                    }
                    className={`rounded-2xl border p-4 text-left transition-colors ${
                      on
                        ? 'border-blue-500/50 bg-blue-500/15'
                        : 'border-slate-700 bg-[#0a0f1c] hover:border-slate-500'
                    }`}
                  >
                    <p className={`text-sm font-bold ${on ? 'text-blue-300' : 'text-white'}`}>
                      {p.label}
                    </p>
                    <p className="text-[11px] text-slate-500 mt-1 leading-relaxed">{p.short}</p>
                  </button>
                );
              })}
            </div>

            <p className="text-xs text-slate-500 max-w-5xl leading-relaxed border border-slate-800 rounded-xl px-4 py-3 bg-[#0a0f1c]/60">
              {protocol.intro}
            </p>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-5xl">
              <section className="rounded-2xl bg-[#0a0f1c] border border-slate-700 p-5 space-y-4">
                <h3 className="text-sm font-bold uppercase tracking-wider" style={{ color: GOLD }}>
                  Bewerber & {protocol.payLabel}
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <label className="block sm:col-span-2">
                    <span className="text-xs text-slate-500 font-semibold">Name</span>
                    <input
                      value={state.interview.candidateName}
                      onChange={(e) => patchInterview({ candidateName: e.target.value })}
                      className="mt-1 w-full bg-[#020617] border border-slate-700 rounded-xl p-3 text-sm text-white"
                    />
                  </label>
                  <label className="block">
                    <span className="text-xs text-slate-500 font-semibold">Gesprächsdatum</span>
                    <input
                      type="date"
                      value={state.interview.talkDate}
                      onChange={(e) => patchInterview({ talkDate: e.target.value })}
                      className="mt-1 w-full bg-[#020617] border border-slate-700 rounded-xl p-3 text-sm text-white"
                    />
                  </label>
                  <label className="block">
                    <span className="text-xs text-slate-500 font-semibold">Wochenstunden</span>
                    <input
                      value={state.interview.weeklyHours}
                      onChange={(e) => patchInterview({ weeklyHours: e.target.value })}
                      className="mt-1 w-full bg-[#020617] border border-slate-700 rounded-xl p-3 text-sm text-white pescout-mono"
                    />
                  </label>
                </div>

                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => patchInterview({ payMode: 'monthly' })}
                    className={`flex-1 rounded-lg px-3 py-2 text-xs font-bold border ${
                      state.interview.payMode === 'monthly'
                        ? 'border-[#E6C299]/50 bg-[#E6C299]/15 text-[#E6C299]'
                        : 'border-slate-700 text-slate-400'
                    }`}
                  >
                    Brutto / Monat
                  </button>
                  <button
                    type="button"
                    onClick={() => patchInterview({ payMode: 'hourly' })}
                    className={`flex-1 rounded-lg px-3 py-2 text-xs font-bold border ${
                      state.interview.payMode === 'hourly'
                        ? 'border-[#E6C299]/50 bg-[#E6C299]/15 text-[#E6C299]'
                        : 'border-slate-700 text-slate-400'
                    }`}
                  >
                    Stundenlohn
                  </button>
                </div>
                {state.interview.payMode === 'monthly' ? (
                  <input
                    value={state.interview.salaryGross}
                    onChange={(e) => patchInterview({ salaryGross: e.target.value })}
                    placeholder={
                      state.interview.protocolRole === 'azubi'
                        ? 'Ausbildungsvergütung brutto / Monat (optional)'
                        : 'Gehaltsvorstellung brutto / Monat'
                    }
                    className="w-full bg-[#020617] border border-slate-700 rounded-xl p-3 text-sm text-white pescout-mono"
                  />
                ) : (
                  <div className="space-y-1">
                    <input
                      value={state.interview.hourlyRate}
                      onChange={(e) => patchInterview({ hourlyRate: e.target.value })}
                      placeholder="Stundenlohn brutto"
                      className="w-full bg-[#020617] border border-slate-700 rounded-xl p-3 text-sm text-white pescout-mono"
                    />
                    <p className="text-[11px] text-slate-500">
                      = {formatEuro(salaryMonthly)} / Monat
                    </p>
                  </div>
                )}
              </section>

              {showEconomics ? (
              <section className="rounded-2xl bg-[#0a0f1c] border border-slate-700 p-5 space-y-4">
                <h3 className="text-sm font-bold uppercase tracking-wider" style={{ color: GOLD }}>
                  Totpunkt & Mindest-Soll
                </h3>
                <p className="text-[11px] text-slate-500 leading-relaxed">
                  Beide Werte kommen aus deinem PeOne-Datensatz und können hier justiert werden.
                </p>

                <div className="rounded-xl border border-slate-700 p-4 space-y-2">
                  <div className="flex justify-between text-xs">
                    <span className="text-slate-400 uppercase font-semibold">
                      Kostendeckung → Totpunkt
                    </span>
                    <span className="pescout-mono text-slate-200">
                      {state.interview.costFactor.toFixed(1)}x
                    </span>
                  </div>
                  <p className="text-[10px] text-slate-500">
                    Datensatz: {datasetCostFactor.toFixed(1)}x
                    {state.interview.costFactorManual ? ' · manuell' : ''}
                  </p>
                  <input
                    type="range"
                    min={2}
                    max={4.5}
                    step={0.1}
                    value={state.interview.costFactor}
                    onChange={(e) =>
                      patchInterview({
                        costFactor: clampCostFactor(Number(e.target.value)),
                        costFactorManual: true,
                      })
                    }
                    className="w-full accent-[#E6C299]"
                  />
                  {state.interview.costFactorManual && (
                    <button
                      type="button"
                      onClick={() =>
                        patchInterview({
                          costFactor: datasetCostFactor,
                          costFactorManual: false,
                        })
                      }
                      className="text-[11px] text-blue-400 font-semibold"
                    >
                      Aus Datensatz ({datasetCostFactor.toFixed(1)}x)
                    </button>
                  )}
                  <p className="text-xl font-extrabold text-white pescout-mono pt-1">
                    {formatEuro(totpunktUmsatz)}
                  </p>
                  <p className="text-[11px] text-slate-500">Totpunkt-Umsatz / Monat (Break-even)</p>
                </div>

                <div
                  className="rounded-xl border p-4 space-y-2"
                  style={{ borderColor: `${GOLD}4D`, backgroundColor: `${GOLD}0D` }}
                >
                  <div className="flex justify-between text-xs">
                    <span className="text-slate-400 uppercase font-semibold">
                      Lohnfaktor → Mindest-Soll
                    </span>
                    <span className="pescout-mono" style={{ color: GOLD }}>
                      {state.interview.multiplier.toFixed(1)}x
                    </span>
                  </div>
                  <p className="text-[10px] text-slate-500">
                    Datensatz: {datasetMultiplier.toFixed(1)}x · Range 3,5–5,5
                    {state.interview.multiplierManual ? ' · manuell' : ''}
                  </p>
                  <input
                    type="range"
                    min={3.5}
                    max={5.5}
                    step={0.1}
                    value={state.interview.multiplier}
                    onChange={(e) =>
                      patchInterview({
                        multiplier: clampMultiplier(Number(e.target.value)),
                        multiplierManual: true,
                      })
                    }
                    className="w-full accent-[#E6C299]"
                  />
                  {state.interview.multiplierManual && (
                    <button
                      type="button"
                      onClick={() =>
                        patchInterview({
                          multiplier: datasetMultiplier,
                          multiplierManual: false,
                        })
                      }
                      className="text-[11px] text-blue-400 font-semibold"
                    >
                      Aus Datensatz ({datasetMultiplier.toFixed(1)}x)
                    </button>
                  )}
                  <p className="text-xl font-extrabold text-white pescout-mono pt-1">
                    {formatEuro(mindestSollUmsatz)}
                  </p>
                  <p className="text-[11px] text-slate-500">
                    Mindest-Sollumsatz / Monat (gesunder Plan)
                  </p>
                </div>
              </section>
              ) : (
                <section className="rounded-2xl bg-[#0a0f1c] border border-slate-700 p-5 space-y-3">
                  <h3 className="text-sm font-bold uppercase tracking-wider" style={{ color: GOLD }}>
                    Kein Umsatz-Check nötig
                  </h3>
                  <p className="text-sm text-slate-400 leading-relaxed">
                    Bei {protocol.label} geht es nicht um Bedienumsatz. Im Protokoll findest du die
                    passenden Leitfragen zu Motivation, Alltag und Teamfit – ohne Totpunkt und
                    Mindest-Soll.
                  </p>
                </section>
              )}
            </div>

            <button
              type="button"
              onClick={() => setProtocolOpen(true)}
              className="w-full max-w-5xl py-4 rounded-2xl border border-blue-500 bg-blue-600 hover:bg-blue-500 text-white font-black text-sm uppercase tracking-widest flex items-center justify-center gap-3 shadow-[0_0_25px_rgba(59,130,246,0.25)]"
            >
              <FileText className="w-5 h-5" />
              Gesprächsprotokoll öffnen ({protocol.label})
            </button>
          </div>
        )}
      </div>

      {/* Gesprächsprotokoll – Vollbild, blätterbar, Druck erst hier */}
      {protocolOpen &&
        createPortal(
          <div className="fixed inset-0 z-[10060] flex flex-col bg-[#0a0f1c]">
            <div className="pescout-protocol-chrome shrink-0 flex items-center justify-between gap-3 px-4 py-3 border-b border-slate-700 bg-[#020617]">
              <div className="min-w-0">
                <p className="text-sm font-bold text-white truncate">
                  Gesprächsprotokoll · {protocol.label}
                  {state.interview.candidateName
                    ? ` · ${state.interview.candidateName}`
                    : ''}
                </p>
                <p className="text-[11px] text-slate-500">
                  Durchblättern · Notizen machen · PDF herunterladen oder drucken
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">
                <button
                  type="button"
                  onClick={() => void handleProtocolDownloadPdf()}
                  disabled={isProtocolPdfDownloading}
                  className="inline-flex items-center gap-2 rounded-xl border border-[#E6C299]/40 bg-gradient-to-b from-[#E6C299] to-[#b48a56] text-black px-3 py-2 text-xs font-black uppercase tracking-wider disabled:opacity-60"
                >
                  <Download size={14} />
                  {isProtocolPdfDownloading ? 'PDF wird erstellt…' : 'Als PDF herunterladen'}
                </button>
                <button
                  type="button"
                  onClick={handleProtocolPrint}
                  disabled={isProtocolPdfDownloading}
                  className="inline-flex items-center gap-2 rounded-xl border border-[#E6C299]/40 bg-[#E6C299]/15 text-[#E6C299] px-3 py-2 text-xs font-bold uppercase tracking-wider disabled:opacity-60"
                >
                  <Printer size={14} /> Drucken
                </button>
                <button
                  type="button"
                  onClick={() => setProtocolOpen(false)}
                  className="rounded-xl border border-slate-600 p-2 text-slate-400 hover:text-white"
                  aria-label="Schließen"
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto">
              <div
                ref={protocolPrintRef}
                id="pescout-protocol-print"
                className="pescout-protocol-print max-w-3xl my-4 md:my-8 mx-4 md:mx-auto bg-white text-slate-900 rounded-2xl shadow-2xl p-6 md:p-10 space-y-8"
              >
                <header className="border-b border-slate-200 pb-5 space-y-3">
                  <h1 className="text-2xl font-black tracking-tight">
                    PeScout · Gesprächsprotokoll
                  </h1>
                  <p className="text-sm font-semibold text-slate-600">{protocol.label}</p>
                  <p className="text-xs text-slate-500 leading-relaxed">{protocol.intro}</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                    <p>
                      <strong>Bewerber:</strong> {state.interview.candidateName || '—'}
                    </p>
                    <p>
                      <strong>Datum:</strong> {state.interview.talkDate || '—'}
                    </p>
                    <p>
                      <strong>Wochenstunden:</strong> {state.interview.weeklyHours || '—'}
                    </p>
                    <p>
                      <strong>{protocol.payLabel}:</strong>{' '}
                      {salaryMonthly > 0
                        ? state.interview.payMode === 'hourly'
                          ? `${formatEuro(hourlyRate)}/Std → ${formatEuro(salaryMonthly)}/Monat`
                          : `${formatEuro(salaryMonthly)} brutto/Monat`
                        : '—'}
                    </p>
                  </div>
                  {showEconomics ? (
                    <>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                        <div className="rounded-xl border border-slate-300 bg-slate-50 p-3">
                          <p className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">
                            Totpunkt ({state.interview.costFactor.toFixed(1)}x)
                          </p>
                          <p className="text-xl font-black pescout-mono">
                            {formatEuro(totpunktUmsatz)}
                          </p>
                          <p className="text-[11px] text-slate-500">Break-even / Monat</p>
                        </div>
                        <div className="rounded-xl border border-amber-300 bg-amber-50 p-3">
                          <p className="text-[10px] uppercase tracking-wider text-amber-800/70 font-bold">
                            Mindest-Soll ({state.interview.multiplier.toFixed(1)}x)
                          </p>
                          <p className="text-xl font-black pescout-mono text-amber-950">
                            {formatEuro(mindestSollUmsatz)}
                          </p>
                          <p className="text-[11px] text-amber-900/60">
                            Gesunder Planumsatz / Monat
                          </p>
                        </div>
                      </div>
                      <p className="text-xs text-slate-500 leading-relaxed">
                        Nutze die Zahlen in Phase 3 transparent: „Damit das Gehalt trägt, brauchen
                        wir mindestens den Totpunkt – und für gesunde Strukturen den Mindest-Soll.“
                      </p>
                    </>
                  ) : (
                    <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600 leading-relaxed">
                      Kein Bedienumsatz-Check in diesem Protokoll. Fokus: passende Leitfragen für{' '}
                      {protocol.label}.
                    </div>
                  )}
                </header>

                {protocol.phases.map((phase, idx) => (
                  <section key={phase.id} className="space-y-3 break-inside-avoid">
                    <div className="flex items-baseline gap-2">
                      <span className="text-xs font-black text-slate-400 pescout-mono">
                        {String(idx + 1).padStart(2, '0')}
                      </span>
                      <h2 className="text-lg font-bold text-slate-900">{phase.title}</h2>
                    </div>
                    <p className="text-xs italic text-slate-500">{phase.tip}</p>
                    <ul className="space-y-3">
                      {phase.questions.map((q) => (
                        <li
                          key={q}
                          className="text-sm text-slate-800 leading-relaxed border-l-4 border-[#E6C299] pl-3 py-1"
                        >
                          {q}
                        </li>
                      ))}
                    </ul>
                    <div>
                      <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">
                        Deine Notizen zu dieser Phase
                      </label>
                      <textarea
                        rows={3}
                        value={state.interview.phaseNotes[phase.id] || ''}
                        onChange={(e) =>
                          patchInterview({
                            phaseNotes: {
                              ...state.interview.phaseNotes,
                              [phase.id]: e.target.value,
                            },
                          })
                        }
                        className="mt-1 w-full rounded-lg border border-slate-300 bg-slate-50 p-3 text-sm text-slate-800"
                        placeholder="Antworten, Beobachtungen, Stichworte…"
                      />
                    </div>
                  </section>
                ))}

                <section className="space-y-3 border-t border-slate-200 pt-6">
                  <h2 className="text-lg font-bold">Scorecard (nach dem Gespräch)</h2>
                  {protocol.scores.map((s) => (
                    <div
                      key={s.key}
                      className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 border border-slate-200 rounded-lg px-3 py-2"
                    >
                      <span className="text-sm">{s.label}</span>
                      <div className="flex gap-1">
                        {[1, 2, 3, 4, 5].map((n) => (
                          <button
                            key={n}
                            type="button"
                            onClick={() =>
                              patchInterview({
                                scores: {
                                  ...state.interview.scores,
                                  [s.key]:
                                    state.interview.scores[s.key] === n ? 0 : n,
                                },
                              })
                            }
                          >
                            <Star
                              size={18}
                              className={
                                n <= state.interview.scores[s.key]
                                  ? 'fill-[#E6C299] text-[#E6C299]'
                                  : 'text-slate-300'
                              }
                            />
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </section>

                <section className="space-y-2 border-t border-slate-200 pt-6">
                  <h2 className="text-lg font-bold">Absprachen</h2>
                  <textarea
                    rows={4}
                    value={state.interview.notes}
                    onChange={(e) => patchInterview({ notes: e.target.value })}
                    className="w-full rounded-lg border border-slate-300 bg-slate-50 p-3 text-sm"
                    placeholder="Probearbeiten, Testmodell, Fristen, nächster Schritt…"
                  />
                </section>

                <p className="text-[10px] text-slate-400 pt-4">
                  PeScout | PreisExpert Premium
                </p>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default PeScoutPage;
